/**
 * Parent layout for the Annotate area — owns the page chrome (title,
 * subtitle, tab nav) shared across Annotation / Adjudication.
 *
 * Header placement mirrors Intelligence (`TaxonomyPage.tsx`): H1 +
 * subtitle, then `SegmentedNav` directly below as part of the same header
 * block. Sub-pages render only their content (no page-level H1) since
 * the layout's title + the active-tab state already convey context.
 *
 * The prototype's separate Annotation Dashboard is unreachable in the
 * source-of-truth `go_lab_standalone.html` (no sidebar entry, no sub-tab);
 * its tabs are duplicates of the per-collection Datasets surface that PR-A
 * ships at `/v4/go-lab/datasets/:collectionId`. We don't mount a Dashboard
 * tab here.
 */
import React from 'react';
import { Outlet } from 'react-router-dom';

import SegmentedNav, { type SegmentedNavItem } from '../../../ui/segmented-nav';

const items: SegmentedNavItem[] = [
  {
    // `key` + URL stay `queue` (long-lived bookmarks rely on the path);
    // only the display label is renamed.
    key: 'queue',
    label: 'Annotation',
    href: '/v4/go-lab/annotate/queue',
    matchPaths: ['/v4/go-lab/annotate/queue'],
  },
  {
    key: 'review',
    label: 'Review',
    href: '/v4/go-lab/annotate/review',
    matchPaths: ['/v4/go-lab/annotate/review'],
  },
];

const AnnotateLayout: React.FC = () => (
  <div className="flex h-full min-h-0 flex-col bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-6 xl:px-8 xl:py-6">
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <h1 className="font-jakarta text-3xl font-extrabold tracking-[-0.04em] text-foreground md:text-4xl">
            Annotate
          </h1>
          <p className="max-w-3xl text-[13px] leading-[1.7] text-muted-foreground">
            Work through your annotation queue, resolve disputed records, and capture snapshots of your annotation state.
          </p>
        </div>
        <SegmentedNav ariaLabel="Annotate sections" items={items} />
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  </div>
);

export default AnnotateLayout;
